import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../api';

interface ToggleFavoriteParams {
  productId: number;
  userId: string;
}

const toggleFavorite = async ({ productId, userId }: ToggleFavoriteParams) => {
  const { data: existingFavorite, error: checkError } = await supabase
    .from('products_favorite')
    .select('id')
    .eq('product', productId)
    .eq('user', userId)
    .maybeSingle();

  if (checkError) {
    throw new Error(`Error checking favorite status: ${checkError.message}`);
  }

  if (existingFavorite) {
    const { error: removeError } = await supabase
      .from('products_favorite')
      .delete()
      .eq('id', existingFavorite.id);

    if (removeError) {
      throw new Error(`Error removing favorite: ${removeError.message}`);
    }

    return { isFavorite: false };
  } else {
    const { error: addError } = await supabase
      .from('products_favorite')
      .insert({
        product: productId,
        user: userId
      });

    if (addError) {
      throw new Error(`Error adding favorite: ${addError.message}`);
    }

    return { isFavorite: true };
  }
};

export default function useToggleProductFavorite() {
  const queryClient = useQueryClient();

  return useMutation(toggleFavorite, {
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(['productFavorites', variables.productId, variables.userId]);
    }
  });
} 